/**
 * PdfLivePreview — Aperçu PDF live avec debounce.
 *
 * Refetch le PDF via /rebilling/preview-pdf à chaque changement de `state`
 * avec debounce 500ms. Utilise <object> pour l'affichage inline (supporté par
 * tous les navigateurs desktop).
 *
 * Props :
 *   - state : state du wizard (lines, notes, payment_terms_days…)
 *   - client : client sélectionné (pour l'entête FACTURÉ À)
 *
 * Comportement :
 *   - Fetch initial dès le mount
 *   - Refetch debounced 500ms après chaque modif de state.lines / totaux
 *   - Loader discret pendant le refresh (ne remplace pas l'ancien PDF pour
 *     éviter le "flash blanc")
 *   - Erreur → toast + placeholder
 */
import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Loader2, FileText, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function PdfLivePreview({ state, client }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const prevUrlRef = useRef(null);

  // Signature minimaliste du state pour trigger useEffect uniquement quand
  // le rendu change vraiment (lines, notes, payment_terms, global_discount).
  const lines = state?.lines || [];
  const sig = JSON.stringify({
    l: lines.map((ln) => ({
      d: ln.description, q: ln.quantity,
      p: ln.unit_price_ht, t: ln.vat_rate,
      dp: ln.discount_percent,
    })),
    n: state?.notes,
    pt: state?.payment_terms_days,
    gd: state?.global_discount_percent,
    c: client?.name,
  });

  useEffect(() => {
    if (!lines.length) return;
    // debounce 500ms
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const r = await api.post(
          "/rebilling/preview-pdf",
          { state, client },
          { responseType: "blob", signal: controller.signal }
        );
        const url = URL.createObjectURL(r.data);
        // Nettoie l'URL précédente APRÈS avoir set la nouvelle (évite le flash)
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = url;
        setPdfUrl(url);
      } catch (e) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          setError(apiError(e));
        }
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return (
    <Card className="border-slate-200 bg-white rounded-xl flex flex-col overflow-hidden h-[720px]"
          data-testid="rebilling-pdf-live">
      <div className="p-3 border-b border-slate-200 flex items-center gap-2 bg-gradient-to-r from-violet-50 to-fuchsia-50">
        <div className="h-7 w-7 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
          <FileText className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">Aperçu facture</div>
          <div className="text-[10px] text-slate-500">
            {loading ? "Mise à jour…" : "Actualisé automatiquement (500ms)"}
          </div>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-violet-600" />}
      </div>

      <div className="flex-1 relative bg-slate-100">
        {!pdfUrl && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Génération de l'aperçu…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
            <AlertCircle className="h-6 w-6 text-amber-500 mb-2" />
            <div className="text-xs text-slate-600 mb-1">Aperçu indisponible</div>
            <div className="text-[10px] text-slate-400 max-w-[280px]">{error}</div>
          </div>
        )}
        {pdfUrl && (
          <object
            data={pdfUrl}
            type="application/pdf"
            className="w-full h-full"
            aria-label="Aperçu PDF"
          >
            <div className="p-4 text-xs text-slate-500 text-center">
              Aperçu non supporté par ce navigateur.
              <a href={pdfUrl} download="apercu.pdf" className="ml-1 text-violet-600 underline">
                Télécharger
              </a>
            </div>
          </object>
        )}
      </div>
    </Card>
  );
}
